/** XeAgents.ai **/
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Lock, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { HealthcareSettings } from "./ClinicPanel";

type FieldType = "text" | "date" | "email" | "phone" | "choice";

interface Field {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  options: string; // comma separated while editing
  hint: string;
}

const TYPE_LABEL: Record<FieldType, string> = { text: "Text", date: "Date", email: "Email", phone: "Phone number", choice: "Pick one" };

// Keys that match Cliniko patient fields are stored there; any other answer is written into the patient's notes.
const PRESETS: Array<Omit<Field, "options" | "hint"> & { hint?: string }> = [
  { key: "email", label: "Email address", type: "email", required: false, hint: "for a confirmation" },
  { key: "phone", label: "Mobile number", type: "phone", required: false },
  { key: "address_1", label: "Street address", type: "text", required: false, hint: "including suburb" },
  { key: "post_code", label: "Post code", type: "text", required: false },
  { key: "medicare", label: "Medicare or insurance number", type: "text", required: false },
  { key: "emergency_contact", label: "Emergency contact", type: "text", required: false, hint: "name and number" },
  { key: "referral_source", label: "How did you hear about us", type: "text", required: false },
  { key: "occupation", label: "Occupation", type: "text", required: false },
];

const DEFAULT_FIELDS: Field[] = [{ key: "email", label: "Email address", type: "email", required: false, options: "", hint: "for a confirmation" }];

function fromSettings(settings: HealthcareSettings | null): Field[] {
  const raw = settings?.registrationFields;
  if (!raw) return DEFAULT_FIELDS;
  return raw.map((f) => ({ key: f.key ?? "", label: f.label ?? "", type: (f.type ?? "text") as FieldType, required: !!f.required, options: (f.options ?? []).join(", "), hint: f.hint ?? "" }));
}

export default function RegistrationFieldsPanel({ settings }: { settings: HealthcareSettings | null }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<Field[]>(DEFAULT_FIELDS);

  useEffect(() => setFields(fromSettings(settings)), [settings]);

  const update = (i: number, patch: Partial<Field>) => setFields((fs) => fs.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, d: -1 | 1) =>
    setFields((fs) => {
      const j = i + d;
      if (j < 0 || j >= fs.length) return fs;
      const next = [...fs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const addPreset = (p: (typeof PRESETS)[number]) => setFields((fs) => [...fs, { ...p, options: "", hint: p.hint ?? "" }]);
  const addCustom = () => setFields((fs) => [...fs, { key: "", label: "", type: "text", required: false, options: "", hint: "" }]);

  const save = useMutation({
    mutationFn: async () => {
      const registrationFields = fields.map((f) => ({
        // A key is only kept when it is a preset's (so it maps to the right system field); custom questions get one from the server.
        ...(PRESETS.some((p) => p.key === f.key) ? { key: f.key } : {}),
        label: f.label.trim(),
        type: f.type,
        required: f.required,
        ...(f.type === "choice" ? { options: f.options.split(",").map((o) => o.trim()).filter(Boolean) } : {}),
        ...(f.hint.trim() ? { hint: f.hint.trim() } : {}),
      }));
      return (await apiRequest("PUT", "/api/healthcare/settings", { registrationFields })).json();
    },
    onSuccess: () => {
      toast({ title: "New-patient questions saved", description: "The next call will ask these." });
      queryClient.invalidateQueries({ queryKey: ["/api/healthcare/settings"] });
    },
    onError: (e: any) => toast({ title: "Could not save", description: e.message, variant: "destructive" }),
  });

  const used = new Set(fields.map((f) => f.key));
  const invalid = fields.some((f) => !f.label.trim() || (f.type === "choice" && f.options.split(",").filter((o) => o.trim()).length < 2));

  return (
    <Card>
      <CardHeader>
        <CardTitle>New-patient questions</CardTitle>
        <CardDescription>
          What the receptionist asks someone who has not been to the clinic before. Name and date of birth are always asked, because they are how returning patients are recognised. Add whatever else your front desk needs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {!settings && <p className="text-sm text-muted-foreground">Save your clinic details first.</p>}

        <div className="space-y-2">
          {["Full name", "Date of birth"].map((label) => (
            <div key={label} className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <Lock className="h-4 w-4 text-muted-foreground" /> {label} <Badge variant="secondary" className="ml-auto">Always asked</Badge>
            </div>
          ))}

          {fields.map((f, i) => (
            <div key={i} className="space-y-2 rounded-md border p-3" data-testid={`registration-field-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="min-w-48 flex-1" value={f.label} maxLength={60} placeholder="Question, for example: Street address" aria-label="Question" onChange={(e) => update(i, { label: e.target.value })} />
                <Select value={f.type} onValueChange={(type) => update(i, { type: type as FieldType })} disabled={f.key === "email" || f.key === "phone"}>
                  <SelectTrigger className="w-40" aria-label="Answer type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TYPE_LABEL) as FieldType[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {TYPE_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={f.required} onCheckedChange={(required) => update(i, { required })} /> Required
                </label>
                <Button size="icon" variant="ghost" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" aria-label="Move down" disabled={i === fields.length - 1} onClick={() => move(i, 1)}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" aria-label="Remove question" onClick={() => setFields((fs) => fs.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {f.type === "choice" && <Input value={f.options} placeholder="Choices, separated by commas: New problem, Check-up, Repeat script" aria-label="Choices" onChange={(e) => update(i, { options: e.target.value })} />}
              <Input value={f.hint} maxLength={120} placeholder="Tip for the receptionist (optional), for example: including suburb" aria-label="Tip" onChange={(e) => update(i, { hint: e.target.value })} />
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Add a question</p>
          <div className="flex flex-wrap gap-2">
            {PRESETS.filter((p) => !used.has(p.key)).map((p) => (
              <Button key={p.key} size="sm" variant="outline" onClick={() => addPreset(p)} disabled={fields.length >= 12}>
                <Plus className="mr-1 h-3 w-3" /> {p.label}
              </Button>
            ))}
            <Button size="sm" variant="secondary" onClick={addCustom} disabled={fields.length >= 12} data-testid="button-add-custom-question">
              <Plus className="mr-1 h-3 w-3" /> Your own question
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Phone defaults to the number the person is calling from, so only add "Mobile number" if you want it confirmed. With Cliniko, answers go into the matching Cliniko field; others are saved in the patient's notes. Only ask for what you need to run the clinic. Up to 12 questions.
          </p>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} disabled={!settings || save.isPending || invalid} data-testid="button-save-registration">
            {save.isPending ? "Saving…" : "Save questions"}
          </Button>
          <Button variant="ghost" onClick={() => setFields(DEFAULT_FIELDS)}>
            Reset to email only
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
